import { ArrowLeft, ChevronRight, Pin, Search } from "lucide-react";
import { type KeyboardEvent as ReactKeyboardEvent, type ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Badge } from "../../components/Badge/Badge";
import { Button } from "../../components/Button/Button";
import { IconButton } from "../../components/IconButton/IconButton";
import { MenuOption } from "../../components/Menu/MenuOption";

export interface CommandPaletteItem {
  value: string;
  icon: ReactNode;
  label: string;

  hint: string;
  onSelect: () => void;
}

export interface CommandPaletteCategory {
  value: string;
  label: string;
  icon: ReactNode;
  items: CommandPaletteItem[];
}

/** Every built-in string, so apps can translate them. Templated ones take the value to insert. */
export interface CommandPaletteLabels {
  dialog: string;
  placeholder: string;
  escape: string;
  noMatches: (query: string) => string;
  allCategories: string;
  pinned: string;
  browse: string;
  pin: (label: string) => string;
  unpin: (label: string) => string;
}

export const DEFAULT_COMMAND_PALETTE_LABELS: CommandPaletteLabels = {
  dialog: "Command palette",
  placeholder: "Search or jump to…",
  escape: "esc",
  noMatches: (query) => `No matches for "${query}"`,
  allCategories: "All categories",
  pinned: "Pinned",
  browse: "Browse",
  pin: (label) => `Pin ${label}`,
  unpin: (label) => `Unpin ${label}`,
};

export interface CommandPaletteProps {
  open: boolean;
  onClose: () => void;
  categories: CommandPaletteCategory[];
  pinnedValues: string[];
  onPinnedValuesChange: (values: string[]) => void;

  maxPins?: number;
  onPinLimitReached?: () => void;

  /** Overrides for the built-in strings (see DEFAULT_COMMAND_PALETTE_LABELS). */
  labels?: Partial<CommandPaletteLabels>;
}

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

function matches(item: CommandPaletteItem, query: string): boolean {
  const q = query.toLowerCase();
  return item.label.toLowerCase().includes(q) || item.hint.toLowerCase().includes(q);
}

export function CommandPalette({
  open,
  onClose,
  categories,
  pinnedValues,
  onPinnedValuesChange,
  maxPins = 5,
  onPinLimitReached,
  labels,
}: Readonly<CommandPaletteProps>) {
  const l = { ...DEFAULT_COMMAND_PALETTE_LABELS, ...labels };
  const [query, setQuery] = useState("");
  const [activeCategoryValue, setActiveCategoryValue] = useState<string | null>(null);
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const dialogRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const previouslyFocused = useRef<HTMLElement | null>(null);

  const allItems = useMemo(() => categories.flatMap((category) => category.items), [categories]);
  const activeCategory = categories.find((category) => category.value === activeCategoryValue);

  const view: "search" | "category" | "browse" = query ? "search" : activeCategory ? "category" : "browse";
  const searchHits = view === "search" ? allItems.filter((item) => matches(item, query)) : [];
  const categoryItems = view === "category" ? (activeCategory?.items ?? []) : [];

  const browseRows = view === "browse" ? categories : [];
  const pinnedItems = pinnedValues
    .map((v) => allItems.find((item) => item.value === v))
    .filter(Boolean) as CommandPaletteItem[];

  const activeListLength =
    view === "search" ? searchHits.length : view === "category" ? categoryItems.length + 1 : browseRows.length;

  // biome-ignore lint/correctness/useExhaustiveDependencies: deliberately watches these three to reset on any view change, not reading them inside.
  useEffect(() => {
    setHighlightedIndex(0);
  }, [view, query, activeCategoryValue]);

  useEffect(() => {
    if (!open) return;

    setQuery("");
    setActiveCategoryValue(null);
    setHighlightedIndex(0);
    previouslyFocused.current = document.activeElement as HTMLElement | null;
    const timer = setTimeout(() => inputRef.current?.focus(), 10);

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
        return;
      }
      if (event.key !== "Tab") return;
      const dialog = dialogRef.current;
      const nodes = dialog?.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);
      if (!nodes || nodes.length === 0) return;
      const first = nodes[0];
      const last = nodes[nodes.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      clearTimeout(timer);
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = previousOverflow;
      previouslyFocused.current?.focus();
    };
  }, [open, onClose]);

  if (!open) return null;

  const togglePin = (value: string) => {
    if (pinnedValues.includes(value)) {
      onPinnedValuesChange(pinnedValues.filter((v) => v !== value));
    } else if (pinnedValues.length >= maxPins) {
      onPinLimitReached?.();
    } else {
      onPinnedValuesChange([...pinnedValues, value]);
    }
  };

  const commitHighlighted = () => {
    if (view === "search") {
      searchHits[highlightedIndex]?.onSelect();
      onClose();
    } else if (view === "category") {
      if (highlightedIndex === 0) {
        setActiveCategoryValue(null);
      } else {
        categoryItems[highlightedIndex - 1]?.onSelect();
        onClose();
      }
    } else {
      setActiveCategoryValue(browseRows[highlightedIndex]?.value ?? null);
    }
  };

  const handleInputKeyDown = (event: ReactKeyboardEvent<HTMLInputElement>) => {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setHighlightedIndex((i) => Math.min(i + 1, activeListLength - 1));
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setHighlightedIndex((i) => Math.max(i - 1, 0));
    } else if (event.key === "Enter") {
      event.preventDefault();
      commitHighlighted();
    }
  };

  const renderItemRow = (item: CommandPaletteItem, index: number) => {
    const pinned = pinnedValues.includes(item.value);
    const isHighlighted = index === highlightedIndex;
    return (
      <div
        key={item.value}
        className={["group/row flex items-center gap-1.5 rounded-lg pr-2", isHighlighted ? "bg-surface-base" : ""].join(
          " ",
        )}
      >
        <MenuOption
          fullWidth={false}
          highlighted={isHighlighted}
          icon={
            <span className="grid size-8 shrink-0 place-items-center rounded-lg border border-border-subtle bg-surface-base text-content-secondary">
              {item.icon}
            </span>
          }
          onHighlight={() => setHighlightedIndex(index)}
          onSelect={() => {
            item.onSelect();
            onClose();
          }}
        >
          {item.label}
        </MenuOption>
        <span className={pinned ? "shrink-0" : "shrink-0 opacity-0 transition-opacity group-hover/row:opacity-100"}>
          <IconButton
            variant="ghost"
            size="sm"
            role="menuitemcheckbox"
            aria-checked={pinned}
            aria-label={pinned ? l.unpin(item.label) : l.pin(item.label)}
            icon={<Pin />}
            onClick={() => togglePin(item.value)}
          />
        </span>
        <Badge variant="neutral" size="compact">
          {item.hint}
        </Badge>
      </div>
    );
  };

  return createPortal(
    <>
      <div aria-hidden="true" className="fixed inset-0 z-[100] bg-surface-overlay backdrop-blur-sm" onClick={onClose} />
      <div className="pointer-events-none fixed inset-0 z-[101] flex items-start justify-center px-4 pt-[12vh]">
        <div
          ref={dialogRef}
          role="dialog"
          aria-modal="true"
          aria-label={l.dialog}
          tabIndex={-1}
          className="pointer-events-auto flex w-full max-w-xl flex-col overflow-hidden rounded-2xl border border-border-input bg-surface-elevated shadow-xl animate-pop-in"
        >
          <div className="flex items-center gap-2.5 border-b border-border-subtle px-4">
            <Search size={18} className="shrink-0 text-content-tertiary" />
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={handleInputKeyDown}
              placeholder={l.placeholder}
              className="flex-1 bg-transparent py-3.5 text-sm text-content-primary outline-none placeholder:text-content-tertiary"
            />
            {}
            <span className="shrink-0 rounded border border-border-input px-1.5 py-0.5 font-mono text-[11px] leading-[16px] text-content-tertiary">
              {l.escape}
            </span>
          </div>
          {/* Everything focusable in the list is a menu item (rows, pin toggles, tiles), so it is a valid menu. */}
          <div
            role="menu"
            aria-label={l.dialog}
            className={["p-1.5", view === "browse" ? "" : "max-h-80 overflow-y-auto"].join(" ")}
          >
            {view === "search" &&
              (searchHits.length > 0 ? (
                searchHits.map((item, index) => renderItemRow(item, index))
              ) : (
                <div className="px-3 py-8 text-center [font:var(--type-body-xs)] text-content-tertiary">
                  {l.noMatches(query)}
                </div>
              ))}

            {view === "category" && activeCategory && (
              <>
                <div className={["mb-1.5 rounded-lg", highlightedIndex === 0 ? "bg-surface-base" : ""].join(" ")}>
                  {}
                  <Button
                    role="menuitem"
                    variant="text"
                    size="sm"
                    leadingIcon={<ArrowLeft size={14} />}
                    onClick={() => setActiveCategoryValue(null)}
                  >
                    {l.allCategories}
                  </Button>
                </div>
                {categoryItems.map((item, index) => renderItemRow(item, index + 1))}
              </>
            )}

            {view === "browse" && (
              <>
                {pinnedItems.length > 0 && (
                  <>
                    <div className="px-1 pb-1 [font:var(--type-eyebrow)] text-content-tertiary uppercase tracking-wide">
                      {l.pinned}
                    </div>
                    <div className="grid grid-cols-3 gap-1.5 px-1 pb-2 sm:grid-cols-5">
                      {pinnedItems.map((item) => (
                        <button
                          key={item.value}
                          type="button"
                          role="menuitem"
                          onClick={() => {
                            item.onSelect();
                            onClose();
                          }}
                          className="flex cursor-pointer flex-col items-center gap-1.5 rounded-xl border border-border-subtle bg-surface-primary px-1.5 py-2.5 transition-colors hover:border-border-strong"
                        >
                          <span className="grid size-8 place-items-center rounded-lg bg-brand-primary-subtle text-brand-primary-on-subtle">
                            {item.icon}
                          </span>
                          <span className="line-clamp-2 text-center [font:var(--type-micro-sm)] text-content-secondary">
                            {item.label}
                          </span>
                        </button>
                      ))}
                    </div>
                  </>
                )}
                <div className="px-1 pb-1 [font:var(--type-eyebrow)] text-content-tertiary uppercase tracking-wide">
                  {l.browse}
                </div>
                {categories.map((category, index) => (
                  <MenuOption
                    key={category.value}
                    highlighted={index === highlightedIndex}
                    icon={
                      <span className="grid size-8 shrink-0 place-items-center rounded-lg border border-border-subtle bg-surface-base text-content-secondary">
                        {category.icon}
                      </span>
                    }
                    trailing={
                      <span className="flex items-center gap-2.5">
                        <span className="[font:var(--type-micro)] text-content-tertiary tabular-nums">
                          {category.items.length}
                        </span>
                        <ChevronRight size={14} className="text-content-tertiary" />
                      </span>
                    }
                    onHighlight={() => setHighlightedIndex(index)}
                    onSelect={() => setActiveCategoryValue(category.value)}
                  >
                    {category.label}
                  </MenuOption>
                ))}
              </>
            )}
          </div>
        </div>
      </div>
    </>,
    document.body,
  );
}
